import { useState } from 'react';
import {
  Settings,
  Store,
  UtensilsCrossed,
  Ticket,
  MapPin,
  Save,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { useSettingsStore } from '../../store/settings';

interface FeatureToggle {
  id: string;
  label: string;
  description: string;
  icon: typeof Store;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  color: string;
}

export function AdminSettings() {
  const {
    foodOrderingEnabled,
    shopEnabled,
    ticketsEnabled,
    mapEnabled,
    setFoodOrderingEnabled,
    setShopEnabled,
    setTicketsEnabled,
    setMapEnabled,
  } = useSettingsStore();

  const [saved, setSaved] = useState(false);

  const featureToggles: FeatureToggle[] = [
    {
      id: 'shop',
      label: 'Merchandise Shop',
      description: 'Allow visitors to browse and purchase merchandise (t-shirts, hats, towels, etc.)',
      icon: Store,
      enabled: shopEnabled,
      onChange: setShopEnabled,
      color: 'text-pink-600',
    },
    {
      id: 'food',
      label: 'Food Ordering',
      description: 'Enable food and drink ordering for track patrons. Turn on when concessions are available.',
      icon: UtensilsCrossed,
      enabled: foodOrderingEnabled,
      onChange: setFoodOrderingEnabled,
      color: 'text-orange-600',
    },
    {
      id: 'tickets',
      label: 'Event Tickets',
      description: 'Allow visitors to view and purchase tickets for upcoming events.',
      icon: Ticket,
      enabled: ticketsEnabled,
      onChange: setTicketsEnabled,
      color: 'text-purple-600',
    },
    {
      id: 'map',
      label: 'Venue Map & Navigation',
      description: 'Show interactive venue map with points of interest and navigation features.',
      icon: MapPin,
      enabled: mapEnabled,
      onChange: setMapEnabled,
      color: 'text-blue-600',
    },
  ];

  const handleToggle = (toggle: FeatureToggle) => {
    toggle.onChange(!toggle.enabled);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Site Settings</h1>
          <p className="text-gray-400">Configure features and site options</p>
        </div>
        {saved && (
          <div className="flex items-center gap-2 px-4 py-2 bg-green-900/50 text-green-400 rounded-lg">
            <CheckCircle size={18} />
            <span>Settings saved</span>
          </div>
        )}
      </div>

      {/* Feature Toggles */}
      <div className="card bg-gray-800 border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <Settings size={24} className="text-gray-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Feature Toggles</h2>
              <p className="text-sm text-gray-400">Enable or disable site features</p>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-700">
          {featureToggles.map((toggle) => {
            const Icon = toggle.icon;
            return (
              <div key={toggle.id} className="p-6 flex items-center justify-between">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg bg-gray-700 ${toggle.color}`}>
                    <Icon size={24} />
                  </div>
                  <div>
                    <h3 className="font-medium text-white">{toggle.label}</h3>
                    <p className="text-sm text-gray-400 mt-1 max-w-md">
                      {toggle.description}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => handleToggle(toggle)}
                  className={`relative w-14 h-8 rounded-full transition-colors ${
                    toggle.enabled ? 'bg-green-600' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                      toggle.enabled ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Food Notice */}
      {!foodOrderingEnabled && (
        <div className="card bg-gray-800 border-gray-700 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-yellow-500 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-yellow-500">Food Ordering Disabled</p>
              <p className="text-sm text-gray-400 mt-1">
                The food ordering feature is currently hidden from visitors. Enable it when track
                concessions are available to allow patrons to order food and drinks.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {featureToggles.map((toggle) => {
          const Icon = toggle.icon;
          return (
            <div
              key={toggle.id}
              className={`card bg-gray-800 border-gray-700 p-4 ${
                !toggle.enabled ? 'opacity-50' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <Icon size={20} className={toggle.color} />
                <span className="text-white font-medium">{toggle.label}</span>
              </div>
              <p className={`mt-2 text-sm ${toggle.enabled ? 'text-green-400' : 'text-gray-500'}`}>
                {toggle.enabled ? 'Enabled' : 'Disabled'}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
